import { Box, Stack, Tooltip, Typography } from '@mui/material'
import { FC } from 'react'
import { tokens } from '@/theme'

export interface ColunaGrafico {
  chave: string
  /** Rótulo curto sob a coluna. */
  rotulo: string
  valor: number
  /** Cor da coluna; sem ela, o laranja Originei. */
  cor?: string
  /** Linha extra sob o rótulo (ex.: R$ da etapa). */
  detalhe?: string
  /** Texto do tooltip; sem ele, o valor formatado. */
  tooltip?: string
}

export interface GraficoColunasProps {
  itens: ColunaGrafico[]
  /** Formata o número em cima da coluna (e no aria-label). */
  formatoValor?: (v: number) => string
  /** Altura útil das colunas, em px. */
  altura?: number
  /** Coluna clicável (ex.: abre o Kanban). */
  aoClicar?: (chave: string) => void
}

/**
 * Gráfico de colunas verticais dos dashboards (conceito BI, 11/09): uma coluna por
 * item, número em cima, rótulo embaixo, escala pelo maior valor. MUI/Box puro,
 * sem lib de gráfico. Os números chegam prontos de quem chama — o componente
 * só desenha.
 */
export const GraficoColunas: FC<GraficoColunasProps> = ({
  itens,
  formatoValor = (v) => String(v),
  altura = 140,
  aoClicar,
}) => {
  const maior = Math.max(1, ...itens.map((i) => i.valor))
  return (
    <Box>
      <Stack
        direction="row"
        spacing={1}
        alignItems="flex-end"
        sx={{ height: altura + 24, borderBottom: `1px solid ${tokens.divider}` }}
      >
        {itens.map((i) => {
          const h = i.valor > 0 ? Math.max((i.valor / maior) * altura, 6) : 3
          const cor = i.cor ?? tokens.brandPrimary
          return (
            <Box
              key={i.chave}
              data-testid="coluna"
              onClick={aoClicar ? () => aoClicar(i.chave) : undefined}
              sx={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 0.5,
                cursor: aoClicar ? 'pointer' : 'default',
                '&:hover [role="img"]': aoClicar ? { filter: 'brightness(0.92)' } : undefined,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  lineHeight: 1,
                  whiteSpace: 'nowrap',
                  color: i.valor > 0 ? tokens.brandInk : tokens.textSecondary,
                }}
              >
                {formatoValor(i.valor)}
              </Typography>
              <Tooltip title={i.tooltip ?? formatoValor(i.valor)} arrow>
                <Box
                  role="img"
                  aria-label={`${i.rotulo}: ${formatoValor(i.valor)}`}
                  sx={{
                    width: '100%',
                    maxWidth: 48,
                    height: h,
                    borderRadius: '5px 5px 0 0',
                    backgroundColor: cor,
                    opacity: i.valor > 0 ? 1 : 0.3,
                    transition: 'height 200ms',
                  }}
                />
              </Tooltip>
            </Box>
          )
        })}
      </Stack>
      <Stack direction="row" spacing={1} sx={{ mt: 0.75 }}>
        {itens.map((i) => (
          <Box key={i.chave} sx={{ flex: 1, minWidth: 0, textAlign: 'center' }}>
            <Typography
              variant="caption"
              component="div"
              sx={{ color: tokens.textSecondary, fontSize: '0.6875rem', lineHeight: 1.2 }}
            >
              {i.rotulo}
            </Typography>
            {i.detalhe && (
              <Typography
                variant="caption"
                component="div"
                sx={{ color: tokens.brandInk, fontWeight: 600, fontSize: '0.6875rem', mt: 0.25 }}
              >
                {i.detalhe}
              </Typography>
            )}
          </Box>
        ))}
      </Stack>
    </Box>
  )
}

export default GraficoColunas
